import React, { useState, useEffect } from "react";
import {
    Card,
    Table,
    Typography,
    Tag,
    Space,
    Button,
    Statistic,
    Row,
    Col,
    Progress,
    Avatar,
    Collapse,
    Alert,
    Spin,
    message,
    Input,
    Select,
    Badge,
} from "antd";
import {
    UserOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    BookOutlined,
    DownloadOutlined,
    ReloadOutlined,
    BarChartOutlined,
    SearchOutlined,
    FilterOutlined,
    TrophyOutlined,
    ClockCircleOutlined,
    FileProtectOutlined,
} from "@ant-design/icons";
import axios from "axios";
import Cookies from "universal-cookie";
import moment from "moment";

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Search } = Input;
const { Option } = Select;

const TeacherCoursesReport = () => {
    const cookies = new Cookies();
    const auth = cookies.get("authToken");
    const config = { headers: { "auth-token": auth, "Content-Type": "application/json" } };

    // States
    const [loading, setLoading] = useState(true);
    const [coursesData, setCoursesData] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [expandedCourses, setExpandedCourses] = useState([]);

    useEffect(() => {
        fetchCoursesReport();
    }, []);

    const fetchCoursesReport = async () => {
        try {
            setLoading(true);
            const response = await axios.get("/api/courses/teacher/all-courses", config);
            setCoursesData(response.data);
            // Auto expand all courses initially
            setExpandedCourses(response.data.map((course) => course.id.toString()));
        } catch (error) {
            console.error("Error fetching courses report:", error);
            message.error("ไม่สามารถโหลดข้อมูลรายงานได้");
        } finally {
            setLoading(false);
        }
    };

    // Export specific course data to CSV
    const exportCourseToCSV = (course) => {
        const csvData = course.students.map((student) => ({
            คอร์ส: course.title,
            "ชื่อ-นามสกุล": student.user_name,
            อีเมล: student.user_email,
            องค์กร: student.organization || "-",
            ตำแหน่ง: student.position || "-",
            สถานะ: getStatusText(student.status),
            วันที่ลงทะเบียน: moment(student.enrollment_date).format("DD/MM/YYYY"),
            วันที่เสร็จสิ้น: student.completion_date ? moment(student.completion_date).format("DD/MM/YYYY") : "-",
            ความคืบหน้า: `${student.progress_percent}%`,
            คะแนนสอบ: student.exam_score ? `${student.exam_score}%` : "-",
            ผลการสอบ: student.exam_score
                ? student.exam_score >= (course.exam_pass_threshold || 70)
                    ? "ผ่าน"
                    : "ไม่ผ่าน"
                : "-",
        }));

        // Convert to CSV string
        const headers = Object.keys(csvData[0] || {});
        const csvContent = [
            headers.join(","),
            ...csvData.map((row) => headers.map((header) => `"${row[header] || ""}"`).join(",")),
        ].join("\n");

        // Download CSV file
        const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `course_${course.title}_${moment().format("YYYYMMDD")}.csv`;
        link.click();
    };

    // Export all courses data to CSV
    const exportAllToCSV = () => {
        const allStudentsData = [];
        coursesData.forEach((course) => {
            course.students.forEach((student) => {
                allStudentsData.push({
                    คอร์ส: course.title,
                    หมวดหมู่: course.category,
                    "ชื่อ-นามสกุล": student.user_name,
                    อีเมล: student.user_email,
                    องค์กร: student.organization || "-",
                    ตำแหน่ง: student.position || "-",
                    สถานะ: getStatusText(student.status),
                    วันที่ลงทะเบียน: moment(student.enrollment_date).format("DD/MM/YYYY"),
                    วันที่เสร็จสิ้น: student.completion_date
                        ? moment(student.completion_date).format("DD/MM/YYYY")
                        : "-",
                    ความคืบหน้า: `${student.progress_percent}%`,
                    คะแนนสอบ: student.exam_score ? `${student.exam_score}%` : "-",
                    ผลการสอบ: student.exam_score
                        ? student.exam_score >= (course.exam_pass_threshold || 70)
                            ? "ผ่าน"
                            : "ไม่ผ่าน"
                        : "-",
                });
            });
        });

        // Convert to CSV string
        const headers = Object.keys(allStudentsData[0] || {});
        const csvContent = [
            headers.join(","),
            ...allStudentsData.map((row) => headers.map((header) => `"${row[header] || ""}"`).join(",")),
        ].join("\n");

        // Download CSV file
        const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `all_courses_report_${moment().format("YYYYMMDD")}.csv`;
        link.click();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "passed":
                return "success";
            case "failed":
                return "error";
            case "in_progress":
                return "processing";
            case "enrolled":
                return "default";
            default:
                return "default";
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case "passed":
                return "ผ่าน";
            case "failed":
                return "ไม่ผ่าน";
            case "in_progress":
                return "กำลังเรียน";
            case "enrolled":
                return "ลงทะเบียนแล้ว";
            default:
                return status;
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "passed":
                return <CheckCircleOutlined />;
            case "failed":
                return <CloseCircleOutlined />;
            case "in_progress":
                return <ClockCircleOutlined />;
            case "enrolled":
                return <BookOutlined />;
            default:
                return <UserOutlined />;
        }
    };

    // Filter students based on search and status
    const getFilteredStudents = (students) => {
        let filtered = [...students];

        // Filter by search text
        if (searchText) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter(
                (student) =>
                    student.user_name?.toLowerCase().includes(searchLower) ||
                    student.user_email?.toLowerCase().includes(searchLower) ||
                    student.organization?.toLowerCase().includes(searchLower)
            );
        }

        // Filter by status
        if (statusFilter !== "all") {
            filtered = filtered.filter((student) => student.status === statusFilter);
        }

        return filtered;
    };

    // Students table columns
    const getStudentColumns = (course) => [
        {
            title: "#",
            key: "index",
            width: 50,
            render: (_, __, index) => index + 1,
        },
        {
            title: "นักเรียน",
            key: "student",
            width: 200,
            render: (_, record) => (
                <Space>
                    <Avatar size="small" icon={<UserOutlined />} />
                    <div>
                        <div style={{ fontWeight: "bold", fontSize: "14px" }}>{record.user_name}</div>
                        <div style={{ fontSize: "11px", color: "#666" }}>{record.user_email}</div>
                    </div>
                </Space>
            ),
        },
        // {
        //     title: "องค์กร/ตำแหน่ง",
        //     key: "organization",
        //     width: 180,
        //     render: (_, record) => (
        //         <div>
        //             <div style={{ fontSize: "13px" }}>{record.organization || "-"}</div>
        //             <div style={{ fontSize: "11px", color: "#666" }}>{record.position || "-"}</div>
        //         </div>
        //     ),
        // },
        {
            title: "สถานะ",
            key: "status",
            width: 100,
            render: (_, record) => (
                <Tag color={getStatusColor(record.status)} icon={getStatusIcon(record.status)}>
                    {getStatusText(record.status)}
                </Tag>
            ),
        },
        {
            title: "ความคืบหน้า",
            key: "progress",
            width: 120,
            render: (_, record) => (
                <Progress
                    percent={record.progress_percent || 0}
                    size="small"
                    strokeColor={
                        record.progress_percent === 100
                            ? "#52c41a"
                            : record.progress_percent >= 50
                            ? "#1890ff"
                            : "#faad14"
                    }
                />
            ),
        },
        // {
        //     title: "คะแนนสอบ",
        //     key: "exam_score",
        //     width: 100,
        //     align: "center",
        //     render: (_, record) => {
        //         if (record.exam_score !== null && record.exam_score !== undefined) {
        //             const passed = record.exam_score >= (course.exam_pass_threshold || 70);
        //             return (
        //                 <div>
        //                     <Tag color={passed ? "success" : "error"}>{record.exam_score}%</Tag>
        //                     <div style={{ fontSize: "10px", marginTop: "2px" }}>
        //                         {passed ? <Text type="success">ผ่าน</Text> : <Text type="danger">ไม่ผ่าน</Text>}
        //                     </div>
        //                 </div>
        //             );
        //         }
        //         return <Text type="secondary">-</Text>;
        //     },
        // },
        {
            title: "วันที่ลงทะเบียน",
            key: "enrollment_date",
            width: 120,
            render: (_, record) => (
                <div>
                    <div>{moment(record.enrollment_date).format("DD/MM/YYYY")}</div>
                    <div style={{ fontSize: "10px", color: "#666" }}>
                        {moment(record.enrollment_date).format("HH:mm")}
                    </div>
                </div>
            ),
        },
        {
            title: "วันที่เสร็จสิ้น",
            key: "completion_date",
            width: 120,
            render: (_, record) =>
                record.completion_date ? (
                    <div>
                        <div>{moment(record.completion_date).format("DD/MM/YYYY")}</div>
                        <div style={{ fontSize: "10px", color: "#666" }}>
                            {moment(record.completion_date).format("HH:mm")}
                        </div>
                    </div>
                ) : (
                    <Text type="secondary">-</Text>
                ),
        },
    ];

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                <Spin size="large" tip="กำลังโหลดข้อมูลรายงาน..." />
            </div>
        );
    }

    return (
        <div style={{ padding: "24px" }}>
            {/* Header */}
            <Card style={{ marginBottom: "24px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            <BarChartOutlined style={{ marginRight: "8px" }} />
                            รายงานคอร์สทั้งหมด
                        </Title>
                        <Text type="secondary">รายงานนักเรียนในแต่ละคอร์สของคุณ</Text>
                    </div>
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={fetchCoursesReport} loading={loading}>
                            รีเฟรช
                        </Button>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={exportAllToCSV}
                            disabled={coursesData.length === 0}
                        >
                            ส่งออกทั้งหมด
                        </Button>
                    </Space>
                </div>
            </Card>

            {/* Filters */}
            {/* <Card style={{ marginBottom: "24px" }}>
                <Row gutter={16} align="middle">
                    <Col xs={24} sm={12} md={8}>
                        <Search
                            placeholder="ค้นหาชื่อนักเรียน, อีเมล, องค์กร"
                            allowClear
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            prefix={<SearchOutlined />}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="กรองสถานะ"
                            value={statusFilter}
                            onChange={setStatusFilter}
                            style={{ width: "100%" }}
                            suffixIcon={<FilterOutlined />}
                        >
                            <Option value="all">ทั้งหมด</Option>
                            <Option value="enrolled">ลงทะเบียนแล้ว</Option>
                            <Option value="in_progress">กำลังเรียน</Option>
                            <Option value="passed">ผ่าน</Option>
                            <Option value="failed">ไม่ผ่าน</Option>
                        </Select>
                    </Col>
                </Row>
            </Card> */}

            {/* Courses Report */}
            {coursesData.length > 0 ? (
                <Collapse activeKey={expandedCourses} onChange={setExpandedCourses} ghost>
                    {coursesData.map((course) => {
                        const filteredStudents = getFilteredStudents(course.students);
                        const passedCount = course.students.filter((s) => s.status === "passed").length;
                        const failedCount = course.students.filter((s) => s.status === "failed").length;
                        const totalStudents = course.students.length;
                        const passRate = totalStudents > 0 ? ((passedCount / totalStudents) * 100).toFixed(1) : 0;

                        return (
                            <Panel
                                key={course.id}
                                header={
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            width: "100%",
                                        }}
                                    >
                                        <div>
                                            <Title level={4} style={{ margin: 0 }}>
                                                <BookOutlined style={{ marginRight: "8px" }} />
                                                {course.title}
                                            </Title>
                                            <Text type="secondary">{course.category}</Text>
                                        </div>
                                        <Space size="large" onClick={(e) => e.stopPropagation()}>
                                            <Statistic
                                                title="นักเรียนทั้งหมด"
                                                value={totalStudents}
                                                valueStyle={{ fontSize: "16px" }}
                                            />
                                            <Statistic
                                                title="ผ่าน"
                                                value={passedCount}
                                                valueStyle={{ fontSize: "16px", color: "#52c41a" }}
                                                suffix={<FileProtectOutlined />}
                                            />
                                            <Statistic
                                                title="ไม่ผ่าน"
                                                value={failedCount}
                                                valueStyle={{ fontSize: "16px", color: "#ff4d4f" }}
                                            />
                                            <div>
                                                <div style={{ fontSize: "12px", color: "#666" }}>อัตราผ่าน</div>
                                                <Progress
                                                    type="circle"
                                                    size={40}
                                                    percent={parseFloat(passRate)}
                                                    format={(percent) => `${percent.toFixed(0)}%`}
                                                    strokeColor={
                                                        passRate >= 80
                                                            ? "#52c41a"
                                                            : passRate >= 60
                                                            ? "#faad14"
                                                            : "#ff4d4f"
                                                    }
                                                />
                                            </div>
                                            <Button
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    exportCourseToCSV(course);
                                                }}
                                            >
                                                ส่งออก
                                            </Button>
                                        </Space>
                                    </div>
                                }
                            >
                                <Card>
                                    {filteredStudents.length > 0 ? (
                                        <>
                                            <div style={{ marginBottom: "16px" }}>
                                                <Alert
                                                    message={`แสดง ${filteredStudents.length} จาก ${totalStudents} นักเรียน`}
                                                    type="info"
                                                    showIcon
                                                />
                                            </div>
                                            <Table
                                                columns={getStudentColumns(course)}
                                                dataSource={filteredStudents}
                                                rowKey={(record) => `${course.id}-${record.user_id}`}
                                                pagination={{
                                                    pageSize: 10,
                                                    showSizeChanger: true,
                                                    showQuickJumper: true,
                                                    showTotal: (total, range) =>
                                                        `${range[0]}-${range[1]} จาก ${total} รายการ`,
                                                }}
                                                size="small"
                                                scroll={{ x: 800 }}
                                            />
                                        </>
                                    ) : (
                                        <Alert
                                            message="ไม่มีนักเรียนที่ตรงกับเงื่อนไขการค้นหา"
                                            type="warning"
                                            showIcon
                                        />
                                    )}
                                </Card>
                            </Panel>
                        );
                    })}
                </Collapse>
            ) : (
                <Alert message="ไม่มีคอร์ส" description="คุณยังไม่มีคอร์สที่สร้างขึ้น" type="info" showIcon />
            )}
        </div>
    );
};

export default TeacherCoursesReport;
